import { t } from 'i18next';
import { useMemo } from 'react';

import {
  NotificationSetting,
  NotificationSettingItem,
  NotificationSettingValue,
  NotificationType
} from '#/entities/notification-setting';
import { Module } from '#/shared/types';

const calculateValue = (
  settings: readonly NotificationSetting[],
  notificationTypes: readonly NotificationType[]
): NotificationSettingValue | null => {
  const foundSettings = settings.filter((setting) =>
    notificationTypes.includes(setting.notificationType)
  );

  if (foundSettings.length === 0) {
    return null;
  }

  const [firstSetting, ...remainingSettings] = foundSettings;
  const firstValue = firstSetting.value;

  if (remainingSettings.length === 0) {
    return firstValue;
  }

  return remainingSettings.every((setting) => setting.value === firstValue) ? firstValue : null;
};

type TGroupedSettings = {
  id: Module;
  title: string;
  items: NotificationSettingItem[];
}[];

export const useGroupedSettings = (settings: NotificationSetting[] = []): TGroupedSettings => {
  return useMemo(() => {
    return [
      {
        id: Module.Admin,
        title: t('module.Admin'),
        items: [
          {
            id: 'admin:roleChange',
            title: t('notificationSettingType.RoleChange'),
            notificationTypes: [NotificationType.RoleAssigned, NotificationType.RoleDeleted],
            value:
              calculateValue(settings, [
                NotificationType.RoleAssigned,
                NotificationType.RoleDeleted
              ]) ?? NotificationSettingValue.All
          }
          // {
          //   id: 'admin:login',
          //   title: t('notificationSettingType.Login'),
          //   notificationTypes: [],
          //   value: calculateValue(settings, []) ?? NotificationSettingValue.All
          // }
        ]
      },
      {
        id: Module.Projects,
        title: t('module.Projects'),
        items: [
          {
            id: 'projects:taskChange',
            title: t('notificationSettingType.TaskChange'),
            notificationTypes: [
              NotificationType.TaskCreated,
              NotificationType.TaskDeleted,
              NotificationType.TaskUpdated,
              NotificationType.TaskMoved,
              NotificationType.TaskStatusChanged,
              NotificationType.TaskCommentAdded,
              NotificationType.TaskAttachmentAdded
            ],
            value:
              calculateValue(settings, [
                NotificationType.TaskCreated,
                NotificationType.TaskDeleted,
                NotificationType.TaskUpdated,
                NotificationType.TaskMoved,
                NotificationType.TaskStatusChanged,
                NotificationType.TaskCommentAdded,
                NotificationType.TaskAttachmentAdded
              ]) ?? NotificationSettingValue.Off
          },
          {
            id: 'projects:assignedTaskChanged',
            title: t('notificationSettingType.AssignedTaskChange'),
            notificationTypes: [
              NotificationType.MyTaskDeleted,
              NotificationType.MyTaskUpdated,
              NotificationType.MyTaskMoved,
              NotificationType.MyTaskStatusChanged,
              NotificationType.MyTaskCommentAdded
            ],
            value:
              calculateValue(settings, [
                NotificationType.MyTaskDeleted,
                NotificationType.MyTaskUpdated,
                NotificationType.MyTaskMoved,
                NotificationType.MyTaskStatusChanged,
                NotificationType.MyTaskCommentAdded
              ]) ?? NotificationSettingValue.Self
          },
          {
            id: 'projects:taskAssign',
            title: t('notificationSettingType.TaskAssign'),
            notificationTypes: [
              NotificationType.AssignedToTask,
              NotificationType.UnassignedFromTask
            ],
            value:
              calculateValue(settings, [
                NotificationType.AssignedToTask,
                NotificationType.UnassignedFromTask
              ]) ?? NotificationSettingValue.All
          },
          {
            id: 'projects:commentMention',
            title: t('notificationSettingType.CommentMention'),
            notificationTypes: [NotificationType.TaskCommentMention],
            value:
              calculateValue(settings, [NotificationType.TaskCommentMention]) ??
              NotificationSettingValue.All
          },
          {
            id: 'projects:projectAccess',
            title: t('notificationSettingType.ProjectAccess'),
            notificationTypes: [NotificationType.ProjectShare],
            value:
              calculateValue(settings, [NotificationType.ProjectShare]) ??
              NotificationSettingValue.All
          },
          {
            id: 'projects:sprintCreated',
            title: t('notificationSettingType.SprintCreated'),
            notificationTypes: [NotificationType.SprintCreated],
            value:
              calculateValue(settings, [NotificationType.SprintCreated]) ??
              NotificationSettingValue.Self
          }
        ]
      },
      {
        id: Module.Mail,
        title: t('module.Mail'),
        items: [
          {
            id: 'mail:newMessage',
            title: t('notificationSettingType.NewMessage'),
            notificationTypes: [NotificationType.NewEmailMessage],
            value:
              calculateValue(settings, [NotificationType.NewEmailMessage]) ??
              NotificationSettingValue.Off
          },
          {
            id: 'mail:replyToImportantMessage',
            title: t('notificationSettingType.ReplyToImportantMessage'),
            notificationTypes: [NotificationType.ImportantNewEmailMessage],
            value:
              calculateValue(settings, [NotificationType.ImportantNewEmailMessage]) ??
              NotificationSettingValue.All
          }
        ]
      },
      {
        id: Module.Disk,
        title: t('module.Disk'),
        items: [
          {
            id: 'disk:diskCommentMention',
            title: t('notificationSettingType.DiskCommentMention'),
            notificationTypes: [NotificationType.DocumentMention, NotificationType.DocumentComment],
            value:
              calculateValue(settings, [
                NotificationType.DocumentMention,
                NotificationType.DocumentComment
              ]) ?? NotificationSettingValue.All
          },
          {
            id: 'disk:diskAccess',
            title: t('notificationSettingType.DiskAccess'),
            notificationTypes: [
              NotificationType.DocumentShare,
              NotificationType.DirectoryShare,
              NotificationType.RoomShare
            ],
            value:
              calculateValue(settings, [
                NotificationType.DocumentShare,
                NotificationType.DirectoryShare,
                NotificationType.RoomShare
              ]) ?? NotificationSettingValue.All
          }
          // {
          //   id: 'disk:diskFull',
          //   title: t('notifications.settings.notificationSettingType.DiskFull'),
          //   notificationTypes: [],
          //   value: calculateValue(settings, []) ?? NotificationSettingValue.Self
          // },
          // {
          //   id: 'disk:availableDocumentChange',
          //   title: t('notifications.settings.notificationSettingType.AvailableDocumentChange'),
          //   notificationTypes: [],
          //   value: calculateValue(settings, []) ?? NotificationSettingValue.Off
          // },
          // {
          //   id: 'disk:myDocumentChange',
          //   title: t('notifications.settings.notificationSettingType.MyDocumentChange'),
          //   notificationTypes: [],
          //   value: calculateValue(settings, []) ?? NotificationSettingValue.Self
          // }
        ]
      },
      {
        id: Module.Calendar,
        title: t('module.Calendar'),
        items: [
          {
            id: 'calendar:eventInvite',
            title: t('notificationSettingType.EventInvite'),
            notificationTypes: [NotificationType.CalendarEventInvite],
            value:
              calculateValue(settings, [NotificationType.CalendarEventInvite]) ??
              NotificationSettingValue.All
          },
          // {
          //   id: 'calendar:EventUpcomingNotification',
          //   title: t('notificationSettingType.EventUpcomingNotification'),
          //   notificationTypes: [NotificationType.CalendarUpcomingEvent],
          //   value:
          //     calculateValue(settings, [NotificationType.CalendarUpcomingEvent]) ??
          //     NotificationSettingValue.All
          // },
          {
            id: 'calendar:EventUpcomingNotification',
            title: t('notificationSettingType.EventUpcomingNotification'),
            notificationTypes: [NotificationType.CalendarUpcomingEvent],
            value:
              calculateValue(settings, [NotificationType.CalendarUpcomingEvent]) ??
              NotificationSettingValue.All
          },
          {
            id: 'calendar:eventChange',
            title: t('notificationSettingType.EventChange'),
            notificationTypes: [
              NotificationType.CalendarEventChange,
              NotificationType.CalendarEventCancel
            ],
            value:
              calculateValue(settings, [
                NotificationType.CalendarEventChange,
                NotificationType.CalendarEventCancel
              ]) ?? NotificationSettingValue.Self
          },
          // {
          //   id: 'calendar:sharedEventChange',
          //   title: t('notifications.settings.notificationSettingType.SharedEventChange'),
          //   notificationTypes: [NotificationType.CalendarEventChange],
          //   value:
          //     calculateValue(settings, [NotificationType.CalendarEventChange]) ??
          //     NotificationSettingValue.Off
          // },
          {
            id: 'calendar:calendarShared',
            title: t('notificationSettingType.CalendarShared'),
            notificationTypes: [NotificationType.CalendarShare],
            value:
              calculateValue(settings, [NotificationType.CalendarShare]) ??
              NotificationSettingValue.All
          }
        ]
      }
    ];
  }, [settings]);
};
